import { createStore } from 'zustand/vanilla';

/**
 * Shared identity-scoped reset registry.
 *
 * Module-level stores that hold data belonging to ONE effective identity
 * (the logged-in user plus whoever they impersonate) register a reset
 * callback here. A single caller — the app shell — reports the current
 * effective identity key via `syncEffectiveIdentity`, and every registered
 * store is reset together when that key genuinely changes.
 *
 * The "genuinely" is the whole point. This generalizes the guard that
 * `FlowChatSessionsProvider`'s hand-rolled store carries today (its
 * `syncIdentity` plus the two dated FIX comments at its `useLayoutEffect`
 * call site) after two rounds of QA-reported "my chat vanished":
 *
 *   1. Resetting on every effect re-run (not just on a real change) wiped
 *      live state on ordinary navigation and on StrictMode's double-invoke.
 *   2. An UNRESOLVED identity is not a new identity. `userId` transiently
 *      falls back to null whenever `/api/me` has no cached data yet — not
 *      only on first load, but on any later queryKey change (a Clerk
 *      sessionId flap, a dev-browser token rotation). A "did it change"
 *      check that treats null as a value sees the flap
 *      `known -> unknown -> known` as TWO changes and wipes state on the
 *      first one.
 *
 * So: callers pass `null` for "don't know yet" instead of re-deriving a
 * guard at each call site, and this module treats null as no news at all —
 * it fires nothing and, critically, does NOT overwrite the last KNOWN key,
 * so that key survives the gap and a later genuinely different key still
 * resets. Do not "simplify" that guard away; it is the fix, not
 * defensiveness.
 *
 * Implementation note: the fan-out uses zustand's own subscribe/notify
 * (`terra/no-hand-rolled-external-store` bans a module-scope listener Set,
 * and a new hand-rolled bus is exactly the precedent GTMOPS-2285 exists to
 * stop). `createStore` from `zustand/vanilla`, not `create` from `zustand`:
 * nothing renders from this state, so it needs no React binding, and the
 * store stays private — the module's only public surface is the three
 * spec-fixed functions below.
 */

type IdentityState = {
  /**
   * The last RESOLVED identity key. `null` means "no identity has ever been
   * reported" (fresh app load) — never "the identity is currently unknown",
   * which is not recorded at all.
   */
  lastEffectiveIdentityKey: string | null;
  /**
   * Bumped once per GENUINE identity change. Registrants reset when it
   * moves, which keeps the "did the identity really change" decision in one
   * place (`syncEffectiveIdentity`) instead of copied into every subscriber.
   */
  resetEpoch: number;
};

const initialState: IdentityState = {
  lastEffectiveIdentityKey: null,
  resetEpoch: 0,
};

const identityStore = createStore<IdentityState>(() => ({ ...initialState }));

/** Live unsubscribes, so the test-only reset can clear the whole registry. */
let registrations: Array<() => void> = [];

/**
 * Register a store's reset callback. Call at module scope, next to the store
 * it resets.
 *
 * @returns an unregister function. After it runs, the callback no longer
 * fires on identity changes.
 */
export function registerIdentityScopedReset(reset: () => void): () => void {
  const unsubscribe = identityStore.subscribe((state, previousState) => {
    if (state.resetEpoch !== previousState.resetEpoch) reset();
  });
  registrations.push(unsubscribe);
  return () => {
    registrations = registrations.filter((entry) => entry !== unsubscribe);
    unsubscribe();
  };
}

/**
 * Report the current effective identity. Safe to call on every render or
 * effect run — it only does work when the identity genuinely changed.
 *
 * - `null` (identity not resolved yet): fires nothing and leaves the
 *   recorded key untouched. This is the flap guard.
 * - First-ever non-null key: recorded, fires nothing. There is no prior
 *   identity's data to clear on a fresh load.
 * - A key equal to the recorded one: fires nothing. Covers benign
 *   re-renders that recompute an equal-by-value, new-by-reference key.
 * - A non-null key that differs from the last recorded non-null key: fires
 *   every registered reset, then records the new key.
 */
export function syncEffectiveIdentity(key: string | null): void {
  if (key === null) return;

  const { lastEffectiveIdentityKey, resetEpoch } = identityStore.getState();
  if (lastEffectiveIdentityKey === key) return;

  identityStore.setState({
    lastEffectiveIdentityKey: key,
    // First-ever key: record it, reset nothing.
    resetEpoch: lastEffectiveIdentityKey === null ? resetEpoch : resetEpoch + 1,
  });
}

/**
 * Test-only: clear the registry AND the recorded key. This module is a
 * singleton, so test files must call it in `beforeEach` — otherwise a
 * previous test's recorded key makes the next test's first key look like a
 * genuine change, and its registered callbacks leak across tests.
 */
export function __resetIdentityScopedResetForTests(): void {
  registrations.forEach((unsubscribe) => {
    unsubscribe();
  });
  registrations = [];
  identityStore.setState({ ...initialState }, true);
}
